import { DateRangeCalendar, ReactSelect, InfoBanner, TableHeader, FiltersButton } from "@services/ui-components";
import { Row, Col, Button } from "react-bootstrap";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PaymentHistoryTable from "./PaymentHistoryTable";
import moment from "moment/moment";
import { useReactiveVar } from "@apollo/client";

import { useGetAllEmployeesQuery } from "@me-team/host/main/salary/graphql/salary.hooks";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useParams } from "react-router-dom";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { DatesRange } from "../../interfaces/interfaces";
import { Filter } from "../Salary/Salary";
import { EPOCH_DATE } from "@services/ui-components/src/DateRangeCalendar/constants";
import { getEmployeeOptions } from "../Salary/constans";

interface Option {
    value: number;
    label: string;
}

const PaymentHistory: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canOpenEmployeeAll = useRoleAccess(CompanyAccess.EmployeeView, {
        scope: PermissionScope.All,
    });
    const canOpenEmployeeSelf = useRoleAccess(CompanyAccess.EmployeeView, {
        scope: PermissionScope.Self,
    });
    const canEditPayment = useRoleAccess(CompanyAccess.PaymentEdit);
    const canDeletePayment = useRoleAccess(CompanyAccess.PaymentDelete);
    const canCreatePayment = useRoleAccess(CompanyAccess.PaymentCreate);
    const hasPaymentRowActions = canEditPayment || canDeletePayment;

    const initialFiltersRange = {
        startDate: moment(new Date(null)).format("YYYY-MM-DD"),
        endDate: moment(new Date(null)).format("YYYY-MM-DD"),
    };
    const [dateRangeFilter, setDateRangeFilter] = useState(initialFiltersRange);

    const [showFilters, setShowFilters] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState(null);
    const { data: employeesData } = useGetAllEmployeesQuery({
        variables: { companyId: +currentCompanyId, branchId: Number(branchId) },
        skip: !currentCompanyId,
    });

    const employeeOptions: Option[] = getEmployeeOptions(employeesData?.user?.company?.employees?.employees);

    const initDateFilter: DatesRange = {
        startDate: null,
        endDate: null,
    };

    const [filter, setFilter] = useState<Filter>({
        dateRangeFilter: initDateFilter,
        employeeFilter: null,
    });

    const toggleFilters = () => setShowFilters(!showFilters);

    const handleEmployeesChange = (selectedOption: Option) => {
        setSelectedEmployees(selectedOption);
    };

    const handleApplyFilter = () => {
        setFilter({
            dateRangeFilter: {
                startDate: dateRangeFilter.startDate !== EPOCH_DATE ? dateRangeFilter.startDate : null,
                endDate: dateRangeFilter.endDate !== EPOCH_DATE ? dateRangeFilter.endDate : null,
            },
            employeeFilter: selectedEmployees?.value ? selectedEmployees?.value : null,
        });
    };

    const handleClearFilter = () => {
        setFilter({
            dateRangeFilter: initDateFilter,
            employeeFilter: null,
        });
        setSelectedEmployees(null);
        setDateRangeFilter(initialFiltersRange);
    };

    return (
        <div className="mb-9">
            <InfoBanner>
                <p className="fs-7 mb-0 violet-hover">
                    {t("A section for keeping a history of salary" + " payments to" + " employees")}
                </p>
            </InfoBanner>

            <TableHeader
                title={t("History of Payments")}
                paths={[
                    { path: `/employees-list/${branchId}`, label: t("Personnel Management") },
                    { path: `/personnel/${currentCompanyId}/salary/${branchId}`, label: t("Salary") },
                    {
                        path: `/personnel/${currentCompanyId}/payment-history/${branchId}`,
                        label: t("History of Payments"),
                    },
                ]}
                colSize={6}
                btnColSize={6}
                buttons={
                    <Row className="d-flex justify-content-end mt-4 mt-lg-0">
                        <Col xs={12} sm={5} md={4} xxl={3}>
                            <FiltersButton
                                filter={filter}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classes="mb-3 mb-lg-0"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <Row className="gx-3 pt-1 mb-4 pb-2 gap-4 gap-lg-0">
                    <Col lg={8} xl={9}>
                        <Row className="gx-3">
                            <Col xs={12} sm={6} className="text-primary mb-3 mb-sm-0">
                                <DateRangeCalendar
                                    dateRangeFilter={dateRangeFilter}
                                    setDateRangeFilter={setDateRangeFilter}
                                    id="paymentHistory-rangeDate-field"
                                    disableValidation={true}
                                />
                            </Col>

                            <Col xs={12} sm={6} className="text-primary">
                                <ReactSelect
                                    name="employees"
                                    id="paymentHistory-employees-field"
                                    value={selectedEmployees}
                                    options={employeeOptions}
                                    placeholder={t("employee")}
                                    onChange={handleEmployeesChange}
                                    isSearchable={true}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Row className="gx-3 h-100">
                            <Col xs={6} className="mb-3 mb-lg-0">
                                <Button
                                    variant="outline-dark"
                                    type="button"
                                    className=" border-grey w-100 h-100 fw-normal"
                                    onClick={handleClearFilter}
                                >
                                    {t("clear")}
                                </Button>
                            </Col>
                            <Col xs={6} className="mb-3 mb-lg-0">
                                <Button variant="primary" className="w-100 h-100 px-1" onClick={handleApplyFilter}>
                                    {t("apply")}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>

            <PaymentHistoryTable
                filter={filter}
                canOpenEmployeeAll={canOpenEmployeeAll}
                canOpenEmployeeSelf={canOpenEmployeeSelf}
                canEditPayment={canEditPayment}
                canDeletePayment={canDeletePayment}
                canCreatePayment={canCreatePayment}
                hasPaymentRowActions={hasPaymentRowActions}
            />
        </div>
    );
};

export default PaymentHistory;
